import styled from 'styled-components';
import * as animations from '../../styles/animations';

export const SecuritySectionWrapper = styled.section<{ $isVisible?: boolean }>`
  padding: 120px 0;
  background: linear-gradient(180deg, #0a0a0a 0%, #0a0a0a 100%);
  position: relative;
  overflow: hidden;
  scroll-margin-top: 100px;
  min-height: 80vh;

  &::before {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 200px;
    background: linear-gradient(180deg, transparent 0%, rgba(10, 10, 10, 0.2) 20%, rgba(10, 10, 10, 0.5) 40%, rgba(10, 10, 10, 0.8) 70%, rgba(10, 10, 10, 1) 100%);
    pointer-events: none;
    z-index: 1;
  }
`;

export const SecurityDemoContainer = styled.div`
  display: grid;
  grid-template-columns: 1.2fr 0.8fr;
  gap: 60px;
  margin-top: 60px;
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

export const SecurityVisualization = styled.div`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 20px;
  padding: 40px;
  backdrop-filter: blur(20px);
`;

export const DemoTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 600;
  color: #fafafa;
  margin-bottom: 32px;
  text-align: center;
`;

export const SecurityPipeline = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const PipelineStep = styled.div<{ $active: boolean; $completed: boolean }>`
  display: flex;
  align-items: center;
  gap: 24px;
  padding: 20px;
  border-radius: 12px;
  background: ${props => props.$active ? 'rgba(255, 68, 68, 0.1)' : 'rgba(255, 255, 255, 0.02)'};
  border: 1px solid ${props => props.$active ? 'rgba(255, 68, 68, 0.3)' : 'rgba(255, 255, 255, 0.08)'};
  transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  opacity: ${props => props.$active ? 1 : 0.6};
  transform: scale(${props => props.$active ? 1.02 : 1});
`;

export const StepNumber = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #ff4444, #ff2222);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  color: white;
  font-size: 1.1rem;
  flex-shrink: 0;
  line-height: 1;
  text-align: center;
  position: relative;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;

  &::before {
    content: '';
    display: inline-block;
    height: 100%;
    vertical-align: middle;
  }

  padding: 0;
  margin: 0;
  box-sizing: border-box;
`;

export const StepContent = styled.div`
  flex: 1;
`;

export const StepTitle = styled.h4`
  font-size: 1.1rem;
  font-weight: 600;
  color: #fafafa;
  margin-bottom: 8px;
`;

export const MessageDemo = styled.div<{ $encrypted: boolean }>`
  padding: 12px 16px;
  background: ${props => props.$encrypted ?
    'linear-gradient(135deg, rgba(255, 68, 68, 0.1), rgba(255, 68, 68, 0.05))' :
    'rgba(255, 255, 255, 0.05)'
  };
  border: 1px solid ${props => props.$encrypted ? 'rgba(255, 68, 68, 0.3)' : 'rgba(255, 255, 255, 0.1)'};
  border-radius: 8px;
  font-family: 'Courier New', monospace;
  font-size: 0.9rem;
  color: ${props => props.$encrypted ? '#ff6666' : '#a0a0a0'};
  position: relative;
  overflow: hidden;

  ${props => props.$encrypted && `
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: -100%;
      width: 100%;
      height: 100%;
      background: linear-gradient(90deg, transparent, rgba(255, 68, 68, 0.3), transparent);
      animation: encryptScan 2s ease-in-out infinite;
    }
  `}

  @keyframes encryptScan {
    0% { left: -100%; }
    100% { left: 100%; }
  }
`;

export const PipelineConnector = styled.div<{ $active: boolean }>`
  width: 2px;
  height: 20px;
  background: ${props => props.$active ?
    'linear-gradient(180deg, #ff4444, #ff2222)' :
    'rgba(255, 255, 255, 0.1)'
  };
  margin: 0 auto;
  transition: all 0.6s ease;
`;

export const TunnelVisualization = styled.div<{ $active: boolean }>`
  height: 40px;
  background: linear-gradient(90deg,
    rgba(255, 68, 68, 0.1),
    rgba(255, 68, 68, 0.3),
    rgba(255, 68, 68, 0.1)
  );
  border-radius: 20px;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: ${props => props.$active ? '0' : '-100%'};
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
    transition: left 1s ease-in-out;
  }
`;

export const SecurityMetrics = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

export const LiveSecurityStatus = styled.div`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 16px;
  padding: 32px;
  backdrop-filter: blur(16px);
`;

export const StatusIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;

  .status-dot {
    width: 12px;
    height: 12px;
    background: #22c55e;
    border-radius: 50%;

    &.pulse {
      animation: statusPulse 2s ease-in-out infinite;
    }
  }

  span {
    font-size: 0.9rem;
    font-weight: 500;
    color: #a0a0a0;
  }

  @keyframes statusPulse {
    0%, 100% { opacity: 1; transform: scale(1); }
    50% { opacity: 0.7; transform: scale(1.1); }
  }
`;

export const SecurityStats = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
`;

export const SecurityStatItem = styled.div`
  text-align: center;
  padding: 16px;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.06);
`;

export const StatValue = styled.div`
  font-size: 1.25rem;
  font-weight: 700;
  color: #ff4444;
  margin-bottom: 4px;
`;

export const SecurityStatLabel = styled.div`
  font-size: 0.8rem;
  color: #a0a0a0;
  font-weight: 500;
`;

export const ComplianceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
`;

export const ComplianceBadge = styled.div<{ $delay: number }>`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  padding: 20px;
  text-align: center;
  opacity: 0;
  animation: ${animations.smoothScale} 0.6s ease-out forwards;
  animation-delay: ${props => props.$delay}ms;

  &:hover {
    border-color: rgba(255, 68, 68, 0.3);
    transform: translateY(-2px);
  }

  transition: all 0.3s ease;
`;

export const BadgeIcon = styled.div`
  width: 32px;
  height: 32px;
  margin: 0 auto 12px;
  color: #ff4444;

  svg {
    width: 100%;
    height: 100%;
  }
`;

export const BadgeTitle = styled.h4`
  font-size: 0.9rem;
  font-weight: 600;
  color: #fafafa;
  margin-bottom: 4px;
`;

export const BadgeDescription = styled.p`
  font-size: 0.75rem;
  color: #a0a0a0;
  margin: 0;
`;
